import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'
import type { Database } from '../types/supabase'

type Cart = Database['public']['Tables']['cart']['Row']
type CartItem = Database['public']['Tables']['cart_items']['Row']

interface CartItemWithDetails extends CartItem {
  booking?: Database['public']['Tables']['bookings']['Row'] | null
}

interface CartContextType {
  cart: Cart | null
  items: CartItemWithDetails[]
  isLoading: boolean
  itemCount: number
  totalAmount: number
  currentClientId: string | null
  addItem: (item: Omit<CartItem, 'id' | 'cart_id' | 'created_at'>) => Promise<void>
  updateItem: (itemId: string, updates: Partial<Pick<CartItem, 'quantity' | 'unit_price' | 'total_price'>>) => Promise<void>
  removeItem: (itemId: string) => Promise<void>
  clearCart: () => Promise<void>
  setCartClient: (clientId: string | null) => Promise<void>
  switchToClientCart: (clientId: string | null) => Promise<void>
  refreshCart: () => Promise<void>
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [cart, setCart] = useState<Cart | null>(null)
  const [items, setItems] = useState<CartItemWithDetails[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentClientId, setCurrentClientId] = useState<string | null>(null)
  const lastFetchTime = useRef<number>(0)

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)
  const totalAmount = items.reduce((sum, item) => sum + Number(item.total_price), 0)

  const fetchCart = useCallback(async (clientId?: string) => {
    if (!user) {
      setCart(null)
      setItems([])
      setIsLoading(false)
      return
    }

    // Debounce frequent fetches (minimum 1 second between fetches)
    const now = Date.now()
    if (now - lastFetchTime.current < 1000) {
      return
    }
    lastFetchTime.current = now

    try {
      setIsLoading(true)

      // Get or create active cart for the client or current user
      let cartQuery = supabase
        .from('cart')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
      
      if (clientId) {
        cartQuery = cartQuery.eq('client_id', clientId)
      }
      
      const { data: cartData, error: cartError } = await cartQuery
      
      if (cartError) {
        // Don't log network errors (common during testing/offline)
        if (cartError.message?.includes('Failed to fetch')) {
          console.log('Cart query skipped (network unavailable)')
        } else {
          console.error('Cart query error:', cartError)
        }
        throw cartError
      }
      
      let existingCart = cartData?.[0] || null

      // Create new cart if none exists
      if (!existingCart) {
        const { data: newCart, error: createError } = await supabase
          .from('cart')
          .insert({
            user_id: user.id,
            client_id: clientId || null,
            status: 'active'
          })
          .select()
          .single()

        if (createError) throw createError
        existingCart = newCart
      }

      setCart(existingCart)

      // Fetch cart items
      const { data: cartItems, error: itemsError } = await supabase
        .from('cart_items')
        .select('*')
        .eq('cart_id', existingCart.id)
        .order('created_at', { ascending: false })

      if (itemsError) throw itemsError

      setItems(cartItems || [])
    } catch (error: unknown) {
      // Don't log network errors (common during testing/offline)
      const err = error as { message?: string; code?: string }
      if (err?.message?.includes('Failed to fetch')) {
        console.log('Cart fetch skipped (network unavailable)')
      } else {
        console.error('Error fetching cart:', error)
        // Only show error toast for actual errors, not for expected scenarios
        if (err?.code && err?.code !== 'PGRST116' && err?.code !== '42P01' && err?.code !== '42501') {
          // Only show toast for unexpected errors
          console.error('Cart error details:', { code: err.code, message: err.message })
          // Don't show toast for now to avoid annoying flashing
          // toast.error('Failed to load cart')
        }
      }
      // Reset to empty state on error
      setCart(null)
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }, [user])

  const addItem = async (item: Omit<CartItem, 'id' | 'cart_id' | 'created_at'>) => {
    if (!cart) {
      toast.error('No active cart found')
      return
    }

    try {
      const { error } = await supabase
        .from('cart_items')
        .insert({
          ...item,
          cart_id: cart.id
        })

      if (error) throw error

      toast.success('Item added to cart')
      await fetchCart()
    } catch (error) {
      console.error('Error adding item to cart:', error)
      toast.error('Failed to add item to cart')
    }
  }

  const updateItem = async (
    itemId: string,
    updates: Partial<Pick<CartItem, 'quantity' | 'unit_price' | 'total_price'>>
  ) => {
    try {
      const { error } = await supabase
        .from('cart_items')
        .update(updates)
        .eq('id', itemId)

      if (error) throw error

      toast.success('Cart updated')
      await fetchCart()
    } catch (error) {
      console.error('Error updating cart item:', error)
      toast.error('Failed to update cart item')
    }
  }

  const removeItem = async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', itemId)

      if (error) throw error

      toast.success('Item removed from cart')
      await fetchCart()
    } catch (error) {
      console.error('Error removing item from cart:', error)
      toast.error('Failed to remove item from cart')
    }
  }

  const clearCart = async () => {
    if (!cart) return

    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('cart_id', cart.id)

      if (error) throw error

      toast.success('Cart cleared')
      await fetchCart()
    } catch (error) {
      console.error('Error clearing cart:', error)
      toast.error('Failed to clear cart')
    }
  }

  const setCartClient = async (clientId: string | null) => {
    if (!cart) return

    try {
      const { error } = await supabase
        .from('cart')
        .update({ client_id: clientId })
        .eq('id', cart.id)

      if (error) throw error

      setCart({ ...cart, client_id: clientId })
    } catch (error) {
      console.error('Error setting cart client:', error)
      toast.error('Failed to update cart client')
    }
  }

  const switchToClientCart = async (clientId: string | null) => {
    setCurrentClientId(clientId)
    await fetchCart(clientId || undefined)
  }

  const refreshCart = async () => {
    await fetchCart(currentClientId || undefined)
  }

  // Initial load
  useEffect(() => {
    fetchCart(currentClientId || undefined)
  }, [fetchCart, currentClientId])

  // Subscribe to cart changes
  useEffect(() => {
    if (!cart) return

    const cartSubscription = supabase
      .channel(`cart:${cart.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cart_items',
          filter: `cart_id=eq.${cart.id}`
        },
        () => {
          fetchCart(currentClientId || undefined)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(cartSubscription)
    }
  }, [cart?.id, currentClientId])

  const value: CartContextType = {
    cart,
    items,
    isLoading,
    itemCount,
    totalAmount,
    currentClientId,
    addItem,
    updateItem,
    removeItem,
    clearCart,
    setCartClient,
    switchToClientCart,
    refreshCart
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

// Helper hook to add booking to cart
export function useAddBookingToCart() {
  const { addItem } = useCart()

  const addBookingToCart = useCallback(
    async (booking: { id: string; title: string; start_time: string; end_time: string; facility_id?: string; facility_name?: string; facilities?: { name: string }; cost?: number; price?: number }) => {
      // Get facility name from the booking or use facility_id
      const facilityName = booking.facilities?.name || booking.facility_name || `Facility ${booking.facility_id}`
      
      // Format the date nicely
      const bookingDate = new Date(booking.start_time).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      
      const timeRange = `${new Date(booking.start_time).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit' 
      })} - ${new Date(booking.end_time).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit' 
      })}`
      
      const description = `${booking.title} - ${facilityName} - ${bookingDate} ${timeRange}`
      
      // Use a default price of 0 if cost is not available
      // The actual price should come from billing data or booking type
      const price = booking.cost || booking.price || 0
      
      await addItem({
        item_type: 'booking',
        item_id: booking.id,
        description,
        quantity: 1,
        unit_price: price.toString(),
        total_price: price.toString(),
        metadata: {
          booking_id: booking.id,
          booking_date: bookingDate,
          start_time: booking.start_time,
          end_time: booking.end_time,
          facility_id: booking.facility_id,
          facility_name: facilityName,
          title: booking.title,
          participants: booking.participants
        }
      })
    },
    [addItem]
  )

  return { addBookingToCart }
}